import { useState, useEffect, useMemo } from 'react';
// @ts-ignore
import debounce from 'lodash.debounce';

type ApiResponse<T, K extends string> = {
  data: {
    [key in K]: T;
  };
};

type UseApiSearchParams<T, K extends string> = {
  apiMethod: (params: any, signal?: AbortSignal) => Promise<ApiResponse<T, K>>;
  /** Параметры поиска (изменяемые) */
  params?: Record<string, any>;
  /** Ключ для поискового запроса (по умолчанию 'search') */
  searchKey?: string;
  /** Ключ в data, по которому находятся нужные данные */
  responseKey: K;
  /** Минимальная длина запроса для выполнения поиска */
  minQueryLength?: number;
  /** Задержка debounce в мс */
  debounceDelay?: number;
  notSendRequest?: boolean;
  initQuery?: string;
};
const initialParams = {}
export const useApiSearch = <T extends any[], K extends string = 'data'>({
  apiMethod,
  params = initialParams,
  searchKey = 'search',
  responseKey,
  minQueryLength = 3,
  debounceDelay = 500,
  notSendRequest = false,
  initQuery = ''
}: UseApiSearchParams<T, K>) => {
  // Состояния
  const [data, setData] = useState<T>([] as unknown as T);
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [query, setQuery] = useState(initQuery);

  // Функция поиска с debounce
  const search = useMemo(
    () => debounce((searchQuery: string, currentParams: any, signal?: AbortSignal) => {
      if (searchQuery.length < minQueryLength || notSendRequest) {
        setData([] as unknown as T);
        return;
      }
      
      setLoading(true);
      const requestParams = {
        ...currentParams,
        [searchKey]: searchQuery
      };

      apiMethod(requestParams, signal)
        .then(({ data }) => {
          setData(data[responseKey] ?? data);
        })
        .catch((e: unknown) => {
          if (e instanceof Error && e.name !== 'AbortError') {
            console.error('Search error:', e);
          }
        })
        .finally(() => setLoading(false));
    }, debounceDelay),
    [notSendRequest]
  );

  // Эффект для выполнения поиска при изменении запроса или параметров
  useEffect(() => {
    const controller = new AbortController();
    setAbortController(controller);

    search(query, params, controller.signal);

    return () => {
      controller.abort();
      search.cancel();
    };
    // search пересоздаётся при смене notSendRequest — без него в зависимостях
    // запрос не повторяется после снятия блокировки и список остаётся пустым
  }, [query, params, search]);

  // Возвращаемые значения
  return {
    /** Найденные данные */
    data,
    /** Флаг загрузки */
    loading,
    /** Текущий поисковый запрос */
    searchQuery: query,
    /** Установить поисковый запрос */
    setSearchQuery: setQuery,
    /** Текущие параметры поиска */
    searchParams: params,
    /** Сбросить состояние */
    reset: () => {
      setQuery('');
      setData([] as unknown as T);
    }
  };
};